// Basic
import { useState } from 'react';
import PropTypes from 'prop-types';
// Store and actions
import actionsPlans from '../../../api/actionsPlans';
import useRootOrganizationStore from '@/app/store/rootOrganization';
import usePlansStore from '@/app/store/modules/plan';
// Libs
import { cn } from '@/common-utils/libraries/tailwind/utils';
// Components
import ParentPlansTable from '../../tables/ParentPlansTable';
import HeaderTitle from '@/common-utils/components/renders/modal/components/headers/HeaderTitle/HeaderTitle';
import Entity from '@/common-utils/components/renders/entity/Entity';
import FooterStep from '@/common-utils/components/renders/modal/components/footers/FooterStep/FooterStep';
import PlanDetailsForm from './component/PlanDetailsForm';
import { Modal, ModalBody, ModalContent } from '@/common-utils/components/ui/modal';
// Common
import plansFields from '../../../fields/plansFields';
import getInitialValueForRowSelected from '@/common-utils/components/renders/dataTable/utils/GetInitialValueForRowSelected';
import { useToast } from '@/common-utils/hooks/use-toast';
import useStepConfig from '@/common-utils/hooks/useStepConfig';
// Style
import '../updatePlan/ModalUpdatePlan.scss';
// Lang
import { nsPlan } from '@/app/i18n/namespaces';
import setFiltersEventName from '@/common-utils/components/renders/dataTable/utils/setFiltersEventName';

/**
 * @param root
 * @param root.isOpen
 * @param root.setIsOpen
 * @returns {JSX} ModalUpdatePlan
 */
function ModalCreatePlan({ isOpen, setIsOpen }) {

    // Constants
    const STEPS_NUMBER = 2;
    const STEP_TITLE_PREFIX = 'modal.create_plan.steps';
    const INIT_FORM_VALUES = {
        productCode: '',
        label: '',
        autoActivate: false
    };

    // State
    const [step, setStep] = useState(0);
    const [formData, setFormData] = useState(INIT_FORM_VALUES);
    const [isValid, setIsValid] = useState(false);
    const [planSelected, setPlanSelected] = useState(getInitialValueForRowSelected());

    // Store
    const rootOrganization = useRootOrganizationStore(s => s.rootOrganization);
    const storeRefetchPlans = usePlansStore(s => s.storeRefetchPlans);

    // Toast
    const { toast } = useToast();

    // Api calls
    const onSuccess = () => {
        setIsOpen(false);
        storeRefetchPlans(true);
        toast({
            description: <Entity namespace={nsPlan} entity='modal.update_plan.success' />,
            variant: 'success'
        });
    };

    const onError = (error) => {
        const code = error.response?.data?.errorCode ?? error.code;
        const errorCode = code === 'ALREADY_EXISTS' ? 'ALREADY_EXISTS' : 'UNKNOWN_ERROR';
        toast({
            description: <Entity namespace={nsPlan} entity={`toast.error.${errorCode}`} />,
            variant: 'danger'
        });
    };

    const createPlan = actionsPlans.createPlan(onSuccess, onError);

    // Functions
    const onFormSubmit = () => {
        const dataToSend = {
            [plansFields.LABEL.key]: formData.label,
            [plansFields.PRODUCT_CODE.key]: formData.productCode,
            [plansFields.SHOULD_AUTO_ACTIVATE_SUBSCRIPTIONS.key]: formData.autoActivate,
            [plansFields.PARENT_SUBSCRIPTION_PLAN.key]: Object.keys(planSelected.rowIds)[0]
        };

        createPlan.mutate({
            organizationId: rootOrganization.uuid,
            dataToSend: dataToSend
        });
    };

    // Hook
    const { stepTitle, disabledNext } = useStepConfig(
        step,
        () => ({
            0: { stepTitle: `${STEP_TITLE_PREFIX}.plan_selection`, disabledNext: !planSelected.rowsData.length },
            1: { stepTitle: `${STEP_TITLE_PREFIX}.form`, disabledNext: !isValid }
        }),
        [planSelected.rowsData, isValid]
    );

    return (
        <Modal open={isOpen} onOpenChange={setIsOpen}>
            <ModalContent className={cn(step !== 0 ? 'max-w-xl' : 'max-w-6xl')}>
                <HeaderTitle title='modal.create_plan.title' namespace={nsPlan} />
                <ModalBody className='modal-max-height overflow-auto'>
                    <Entity
                        entity={stepTitle}
                        data={{ step: step + 1, maxStep: STEPS_NUMBER }}
                        namespace={nsPlan}
                        className={'mb-2 flex'}
                    />
                    {
                        step === 0 &&
                        <ParentPlansTable
                            filtersEventName={setFiltersEventName('parent_plans', true)}
                            singleSelection={true}
                            rowsSelected={planSelected}
                            setRowsSelected={setPlanSelected}
                        />
                    }
                    {
                        step === 1 &&
                        <PlanDetailsForm
                            formData={formData}
                            setFormData={setFormData}
                            setIsValid={setIsValid}
                            isLoading={createPlan.isLoading}
                        />
                    }
                </ModalBody>
                <FooterStep
                    step={step}
                    setStep={setStep}
                    stepsNumber={STEPS_NUMBER}
                    onValidate={onFormSubmit}
                    isLoading={createPlan.isLoading}
                    disableValidation={disabledNext}
                />
            </ModalContent>
        </Modal>
    );
}

ModalCreatePlan.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    setIsOpen: PropTypes.func.isRequired
};

export default ModalCreatePlan;